import {
  Avatar,
  AVATAR_SIZES,
  DetailCard,
  FeatureToggle,
  State,
} from "@neru/base/components";
import { config } from "@neru/base/config";
import {
  FEATURE_FLAGS,
  namedRoute,
  useCurrentAccount,
  useFilter,
  useGetProductQuery,
  useNavigateTo,
  usePlansAndPricingRoute,
  useProtectUnpublishedProduct,
  useValidSession,
} from "@neru/base/hooks";
import { ProductType_Enum, STATUS_TO_CONNOTATION_MAP } from "@neru/base/types";
import { componentLoader, getAccountId, Text } from "@neru/base/utils";
import { TIER_HEADERS } from "@neru/base/utils/tiers";
import {
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Column,
  H1,
  Icon,
  type IconType,
  Layout,
  Phrase,
  Row,
  Spacer,
  TabPane,
  Tabs,
  Tooltip,
} from "@neru/ui";
import { format } from "date-fns";
import React, { lazy, useMemo } from "react";
import { Link } from "react-router-dom";

import { ProductBanner } from "../ProductBanner";
import { Overview } from "./Overview/Overview";
const DeployProduct = lazy(() =>
  componentLoader(() => import("./DeployProduct/DeployProduct")),
);
const GetCode = lazy(() => componentLoader(() => import("./GetCode/GetCode")));

interface ProductProps {
  productId: string;
}

export interface DeployProductData {
  instanceName: string;
  regionId: string;
}

export interface FormValues {
  deleted_at: boolean;
  id: string;
}

export interface CreateProjectData {
  projectName: string;
}

interface ProductMetaDataProps {
  createdAt: string;
  id: string;
  name?: string;
  releasesCount?: number;
  updatedAt: string;
}

export const PRODUCT_RULES = {
  eq: ["id"],
  is_null: ["deleted_at"],
};

const IconWrapper = ({ type }: { type: IconType }) => (
  <div
    className={`
      flex h-12 min-h-12 w-12 min-w-12 items-center justify-center rounded-full
      bg-cta-100
    `}
  >
    <Icon connotation="cta" type={type} />
  </div>
);

const ProductMetaData = ({
  createdAt,
  id,
  name = "",
  releasesCount = 0,
  updatedAt,
}: ProductMetaDataProps) => {
  const { tier, usage } = useCurrentAccount();

  const navigateToPlansAndPricing = usePlansAndPricingRoute();

  const limits = tier?.limits
    .filter(
      (limit) =>
        limit.name === TIER_HEADERS.deploymentInstances ||
        limit.name === TIER_HEADERS.marketplaceWorkspaces,
    )
    .reduce(
      (acc, { name, value }) => ({ ...acc, [name]: value }),
      {} as Record<TIER_HEADERS, string>,
    );

  return (
    <Card
      className={`
        h-min w-full p-8

        lg:!mx-0 lg:!mb-6 lg:!w-min

        md:!mb-0
      `}
      data-testid="product-meta-info"
    >
      <Row>
        <IconWrapper type="sparkles-line" />
        <Column className="ml-4 text-nowrap">
          <DetailCard
            accent
            title="Created"
            value={format(new Date(createdAt), "dd MMMM yyyy")}
          />
        </Column>
      </Row>
      <Row className="mt-6">
        <IconWrapper type="compose-line" />
        <Column className="ml-4 text-nowrap">
          <DetailCard
            accent
            title="Updated"
            value={format(new Date(updatedAt), "dd MMMM yyyy")}
          />
        </Column>
      </Row>
      <Row className="mt-6">
        <IconWrapper type="tag-line" />
        <Column className="ml-4">
          <DetailCard
            accent
            data-testid="releases-count"
            title="Releases"
            url={namedRoute("/:id/releases", { id, name })}
            value={`${releasesCount}`}
          />
        </Column>
      </Row>

      <FeatureToggle flagName={FEATURE_FLAGS.documentation}>
        <Row className="mt-6">
          <IconWrapper type="file-line" />
          <Column className="ml-4 justify-center text-nowrap">
            <Link
              role="link"
              to={namedRoute("/:id/documentation", { id, name })}
            >
              See documentation
            </Link>
          </Column>
        </Row>
      </FeatureToggle>
      <FeatureToggle flagName={FEATURE_FLAGS.support}>
        <Row className="mt-6">
          <IconWrapper type="envelope-line" />
          <Column className="ml-4 justify-center text-nowrap">
            Contact us for support
          </Column>
        </Row>
      </FeatureToggle>
      {limits &&
        limits[TIER_HEADERS.deploymentInstances] &&
        limits[TIER_HEADERS.marketplaceWorkspaces] && (
          <>
            <Row className="mt-6">
              <IconWrapper type="ai-line" />
              <Column className="ml-4">
                <DetailCard
                  accent
                  data-testid="deployed-instances"
                  title="Deployed instances"
                  value={`${usage.instances}/${limits[TIER_HEADERS.deploymentInstances]}`}
                />
              </Column>
            </Row>
            <Row className="mt-6">
              <IconWrapper type="code-line" />
              <Column className="ml-4">
                <DetailCard
                  accent
                  data-testid="template-availability"
                  title="Template availability"
                  value={`${usage.workspaces}/${limits[TIER_HEADERS.marketplaceWorkspaces]}`}
                />
              </Column>
            </Row>
            <Column className="mt-6">
              <Button
                label="Upgrade your plan"
                layout="filled"
                onClick={navigateToPlansAndPricing}
              />
            </Column>
          </>
        )}
    </Card>
  );
};

export const ProductDetails = ({ productId }: ProductProps) => {
  const { canPublishProducts } = useCurrentAccount();
  const hasValidSession = useValidSession();
  const navigateTo = useNavigateTo();

  const [productFilter] = useFilter<FormValues>(PRODUCT_RULES, {
    deleted_at: true,
    id: productId,
  });

  const productQuery = useMemo(
    () => ({ where: productFilter }),
    [productFilter],
  );

  const { data, error, loading } = useGetProductQuery(productQuery);
  useProtectUnpublishedProduct(data);

  const {
    created_at,
    id,
    isLive,
    isPublic,
    name,
    owner,
    owner_name,
    ProductVersion,
    programming_language,
    type,
    updated_at,
  } = data ?? {};

  const {
    code_template_enabled,
    deploy_enabled,
    icon,
    id: versionId,
    long_description = "",
    setup_configuration,
    short_description,
    status,
    version,
    VersionTags,
  } = ProductVersion ?? {};

  const manageProductVersions = () => {
    navigateTo("/:id/versions", { id, name });
  };

  const manageProductClients = () => {
    navigateTo("/:id/clients", { id, name });
  };

  const workspaceConfiguration = [
    {
      description: "Give a unique name to your workspace",
      name: "vonageApplicationName",
      title: "Workspace name",
      type: "VONAGE_APPLICATION_NAME",
      withRegionSelector: true,
    },
    ...(setup_configuration?.data ?? []),
  ];

  const instanceConfiguration = [
    {
      description: "Give a unique name to your instance",
      name: "vonageApplicationName",
      title: "Instance name",
      type: "VONAGE_APPLICATION_NAME",
      withRegionSelector: true,
    },
    ...(setup_configuration?.data ?? []),
  ];

  const isUnlisted = type === ProductType_Enum.Unlisted;
  const isProductOwner = getAccountId() === owner;

  const getProductTypeIcon = () => {
    if (type === ProductType_Enum.Private)
      return (
        <Tooltip text="Private product">
          <Icon connotation="info" size="small" type="lock-solid" />
        </Tooltip>
      );

    if (type === ProductType_Enum.Unlisted)
      return (
        <Tooltip text="Unlisted product">
          <Icon connotation="info" size="small" type="eye-hide-solid" />
        </Tooltip>
      );

    return null;
  };

  return (
    <Layout>
      <Breadcrumbs>
        <Link to="/">Home</Link>
        <>{name}</>
      </Breadcrumbs>

      <State
        emptyStateMessage={`Product with ID ${productId} not found`}
        errorMessage={error}
        isData={Boolean(data)}
        loading={loading}
      >
        {data ? (
          <Column className="lg:!flex-row">
            <Column className="grow-[3] justify-start">
              <Row>
                <Column className="grow" data-testid="product-card">
                  <Row>
                    <Column className="justify-start pr-4 pt-4">
                      <Avatar
                        fallbackImage={`${
                          import.meta.env.VITE_PUBLIC_URL
                        }/img/no-product-image.png`}
                        size={AVATAR_SIZES.large}
                        src={`${config.marketCDNUrl}/${icon}`}
                        title={name}
                      />
                    </Column>
                    <Column className="grow justify-start">
                      <Row className="mb-6 items-start">
                        <Column className="grow">
                          <Row
                            className={`
                            max-w-[75vw] flex-nowrap overflow-hidden
                          `}
                          >
                            <H1
                              className={`
                                mb-0 mt-2 whitespace-nowrap pb-2 pt-1
                                leading-none
                              `}
                            >
                              {Text.truncateStringByWord(name ?? "", 60)}
                            </H1>
                          </Row>
                          <Row className="w-min flex-nowrap">
                            {VersionTags?.map(({ Tag }) => (
                              <React.Fragment key={Tag.name}>
                                <Badge
                                  data-testid={`tag-${Tag.name}`}
                                  layout="soft"
                                >
                                  {Tag.name}
                                </Badge>
                                <Spacer size="xs" />
                              </React.Fragment>
                            ))}
                          </Row>
                        </Column>
                        {hasValidSession && isProductOwner && (
                          <Column className="mt-4 grow justify-start pt-2">
                            <Row className="items-center justify-end">
                              {getProductTypeIcon()}

                              <Spacer />
                              {status && !(isLive && isPublic) ? (
                                <Badge
                                  connotation={
                                    STATUS_TO_CONNOTATION_MAP[status]
                                  }
                                  data-testid={`status-version-${version}`}
                                  layout="soft"
                                  shape="pill"
                                >
                                  {Text.toSentenceCase(status, true)}
                                </Badge>
                              ) : null}
                            </Row>
                          </Column>
                        )}
                      </Row>
                      <Row className="items-center">
                        <Column className="min-w-full">
                          <Row className="flex-wrap items-center justify-between">
                            <Column className="mb-4">
                              <Row className="flex-nowrap items-center">
                                <Icon size="small" type="tag-line" />
                                <Spacer size="xs" />
                                <Tooltip placement="top" text="Product version">
                                  <Phrase className="whitespace-nowrap">
                                    {version}
                                  </Phrase>
                                </Tooltip>
                                <Spacer size="xl" />
                                <Icon size="small" type="star-line" />
                                <Spacer size="xs" />
                                <Tooltip placement="top" text="Product author">
                                  <Phrase className="whitespace-nowrap">
                                    {owner_name ?? owner}
                                  </Phrase>
                                </Tooltip>
                                <Spacer size="xl" />
                                <Icon size="small" type="curly-brackets-line" />
                                <Spacer size="xs" />
                                <Tooltip
                                  placement="top"
                                  text="Product programming language"
                                >
                                  <Phrase className="whitespace-nowrap">
                                    {programming_language ?? "-"}
                                  </Phrase>
                                </Tooltip>
                              </Row>
                            </Column>
                            {hasValidSession &&
                              isProductOwner &&
                              canPublishProducts && (
                                <Column className="mb-4 grow items-end">
                                  <Row className="flex-nowrap">
                                    <Tooltip text="Product clients overview">
                                      <Button
                                        data-testid="view-product-clients"
                                        icon="group-line"
                                        label="Clients"
                                        layout="outlined"
                                        onClick={manageProductClients}
                                        shape="rounded"
                                        size="condensed"
                                      />
                                    </Tooltip>
                                    <Spacer size="sm" />
                                    <Tooltip text="Manage product versions">
                                      <Button
                                        data-testid="manage-product-versions"
                                        icon="list-line"
                                        label="Versions"
                                        layout="outlined"
                                        onClick={manageProductVersions}
                                        shape="rounded"
                                        size="condensed"
                                      />
                                    </Tooltip>
                                  </Row>
                                </Column>
                              )}
                          </Row>
                        </Column>
                      </Row>

                      <Phrase>{short_description}</Phrase>
                      <Spacer size="xl" vertical />

                      <ProductBanner
                        isLive={isLive}
                        isProductOwner={isProductOwner}
                        isPublic={isPublic}
                        isUnlisted={isUnlisted}
                      />
                    </Column>
                  </Row>
                </Column>
              </Row>
              <Row className="mt-8">
                <Column className="grow-[5]">
                  <Tabs defaultActiveKey="Overview" defer>
                    <TabPane tab="Overview">
                      <Overview description={long_description} />
                    </TabPane>
                    {code_template_enabled ? (
                      <TabPane tab="Get Code">
                        <GetCode
                          configuration={workspaceConfiguration}
                          productId={productId}
                          productName={name ?? "[unnamed]"}
                          versionId={versionId}
                        />
                      </TabPane>
                    ) : null}
                    {deploy_enabled ? (
                      <TabPane tab="Deploy Code">
                        <DeployProduct
                          configuration={instanceConfiguration}
                          product={data}
                        />
                      </TabPane>
                    ) : null}
                  </Tabs>
                </Column>
              </Row>
            </Column>
            <Row
              className={`
                mt-4 justify-start

                lg:!ml-16 lg:!mt-0
              `}
            >
              <ProductMetaData
                createdAt={created_at}
                id={id}
                name={name}
                releasesCount={data.Releases.aggregate?.count}
                updatedAt={updated_at}
              />
            </Row>
          </Column>
        ) : null}
      </State>
    </Layout>
  );
};
